import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheckCircle, FiUpload } from "react-icons/fi";
import Notification from "../../../components/ui/Notification";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import EmptyState from "../../../components/ui/EmptyState";
import {
  getClinicProfile,
  updateClinicProfile,
  uploadClinicLogo,
  getLogoFullUrl,
} from "../../../api/clinic";
import { useAuth } from "../../../context/AuthContext";
import { useClinicTheme } from "../../../context/ClinicThemeContext";
import { publishClinicLogo } from "../../../utils/clinicLogoSync";
import {
  DEFAULT_VITAL_PREFERENCES,
  parseVitalPreferences,
} from "../../../utils/vitalPreferences";
import {
  DEFAULT_PROTOCOL_PREFERENCES,
  parseProtocolPreferences,
} from "../../../utils/protocolPreferences";
import {
  COLOR_THEME_OPTIONS,
  DEFAULT_COLOR_THEME_PREFERENCES,
  getThemePreviewColors,
  parseColorThemePreferences,
} from "../../../utils/colorThemePreferences";

const VITAL_OPTIONS = [
  { key: "enableWeight", label: "Pesha" },
  { key: "enableBloodPressure", label: "Presioni i gjakut" },
  { key: "enableTemperature", label: "Temperatura" },
  { key: "enableHeartRate", label: "Rrahjet e zemrës" },
];

const ALLOWED_LOGO_TYPES = ".jpg,.jpeg,.png,.gif,.webp";

function getInitials(name) {
  if (!name || !name.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function fieldValue(profile, key) {
  if (!profile) return "";
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return profile[key] ?? profile[pascal] ?? "";
}

function ProfileRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-200 py-3.5">
      <span className="shrink-0 text-sm text-slate-500">{label}</span>
      <div className="min-w-0 text-right text-sm font-semibold text-slate-900">{children}</div>
    </div>
  );
}

function ProfileText({ label, children }) {
  return (
    <label className="block py-3">
      <span className="mb-1 block text-sm text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const fieldClass =
  "w-full max-w-[16rem] bg-transparent text-right text-sm font-semibold text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400";

const textFieldClass =
  "w-full border-b border-slate-200 bg-transparent px-0.5 py-2 text-left text-sm font-medium leading-6 text-slate-900 outline-none transition-colors placeholder:font-normal placeholder:text-slate-400 focus:border-slate-400 whitespace-pre-wrap break-words [overflow-wrap:anywhere]";

function PrefCheckbox({ checked, disabled, onChange, label }) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-clinic-600 focus:ring-clinic-500"
    />
  );
}

function prefsFromProfile(data) {
  return {
    vitalPrefs: data ? parseVitalPreferences(data) : { ...DEFAULT_VITAL_PREFERENCES },
    protocolPrefs: data ? parseProtocolPreferences(data) : { ...DEFAULT_PROTOCOL_PREFERENCES },
    colorThemePrefs: data ? parseColorThemePreferences(data) : { ...DEFAULT_COLOR_THEME_PREFERENCES },
    showPdfWatermark: data?.showPdfWatermark !== false,
  };
}

function sameVitals(a, b) {
  return VITAL_OPTIONS.every(({ key }) => !!a[key] === !!b[key]);
}

function sameProtocol(a, b) {
  return (
    !!a.useProtocolNumber === !!b.useProtocolNumber &&
    !!a.allowNurseToSet === !!b.allowNurseToSet &&
    !!a.allowDoctorToSet === !!b.allowDoctorToSet &&
    String(a.nextProtocolNumber || "") === String(b.nextProtocolNumber || "")
  );
}

export default function ClinicProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setTheme, refreshFromProfile } = useClinicTheme();
  const clinicId = user?.clinicId ?? user?.ClinicId;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState({ visible: false, type: "info", message: "" });
  const [form, setForm] = useState({ name: "", address: "", phone: "", description: "" });
  const [vitalPrefs, setVitalPrefs] = useState({ ...DEFAULT_VITAL_PREFERENCES });
  const [protocolPrefs, setProtocolPrefs] = useState({ ...DEFAULT_PROTOCOL_PREFERENCES });
  const [colorThemePrefs, setColorThemePrefs] = useState({ ...DEFAULT_COLOR_THEME_PREFERENCES });
  const [showPdfWatermark, setShowPdfWatermark] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoStamp, setLogoStamp] = useState(() => Date.now());
  const [saving, setSaving] = useState(false);

  const hasClinic = !!clinicId;

  const fetchProfile = useCallback(async () => {
    if (!hasClinic) return;
    setLoading(true);
    try {
      const data = await getClinicProfile();
      setProfile(data);
      setForm({
        name: fieldValue(data, "name"),
        address: fieldValue(data, "address"),
        phone: fieldValue(data, "phone"),
        description: fieldValue(data, "description"),
      });
      const prefs = prefsFromProfile(data);
      setVitalPrefs(prefs.vitalPrefs);
      setProtocolPrefs(prefs.protocolPrefs);
      setColorThemePrefs(prefs.colorThemePrefs);
      setShowPdfWatermark(prefs.showPdfWatermark);
    } catch (err) {
      setNotif({
        visible: true,
        type: "error",
        message: err.response?.data?.message ?? err.response?.data ?? "Dështoi ngarkimi i profilit të klinikës.",
      });
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [hasClinic]);

  useEffect(() => {
    if (!hasClinic) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchProfile();
  }, [hasClinic, fetchProfile, navigate]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    publishClinicLogo(url);
    return () => {
      publishClinicLogo(null);
      window.setTimeout(() => URL.revokeObjectURL(url), 500);
    };
  }, [logoFile]);

  const showNotif = (type, message) => setNotif({ visible: true, type, message });

  const saved = {
    name: fieldValue(profile, "name"),
    address: fieldValue(profile, "address"),
    phone: fieldValue(profile, "phone"),
    description: fieldValue(profile, "description"),
  };
  const textDirty =
    form.name.trim() !== saved.name ||
    form.address.trim() !== saved.address ||
    form.phone.trim() !== saved.phone ||
    form.description.trim() !== saved.description;
  const savedPrefs = prefsFromProfile(profile);
  const prefsDirty =
    !sameVitals(vitalPrefs, savedPrefs.vitalPrefs) ||
    !sameProtocol(protocolPrefs, savedPrefs.protocolPrefs) ||
    (colorThemePrefs.themeId || "") !== (savedPrefs.colorThemePrefs.themeId || "") ||
    showPdfWatermark !== savedPrefs.showPdfWatermark;
  const dirty = textDirty || prefsDirty || Boolean(logoFile);

  const setField = (key) => (e) => setForm((current) => ({ ...current, [key]: e.target.value }));

  const handleSave = async () => {
    if (!dirty || saving) return;
    if (!form.name.trim()) {
      showNotif("error", "Emri i klinikës është i detyrueshëm.");
      return;
    }
    setSaving(true);
    try {
      let updated = profile;
      if (logoFile) {
        updated = await uploadClinicLogo(logoFile);
        setLogoFile(null);
        setLogoStamp(Date.now());
      }
      if (textDirty || prefsDirty) {
        updated = await updateClinicProfile({
          ...(textDirty
            ? {
                name: form.name.trim(),
                address: form.address.trim(),
                phone: form.phone.trim(),
                description: form.description.trim(),
              }
            : {}),
          ...(prefsDirty
            ? {
                vitalPreferences: vitalPrefs,
                protocolPreferences: protocolPrefs,
                colorThemePreferences: colorThemePrefs,
                showPdfWatermark,
              }
            : {}),
        });
      }
      if (prefsDirty) {
        setTheme(colorThemePrefs.themeId);
        refreshFromProfile(updated);
      }
      setProfile(updated);
      setForm({
        name: fieldValue(updated, "name"),
        address: fieldValue(updated, "address"),
        phone: fieldValue(updated, "phone"),
        description: fieldValue(updated, "description"),
      });
      const nextPrefs = prefsFromProfile(updated);
      setVitalPrefs(nextPrefs.vitalPrefs);
      setProtocolPrefs(nextPrefs.protocolPrefs);
      setColorThemePrefs(nextPrefs.colorThemePrefs);
      setShowPdfWatermark(nextPrefs.showPdfWatermark);
      showNotif("success", "Profili i klinikës u ruajt.");
    } catch (err) {
      showNotif("error", err.response?.data?.message ?? err.response?.data ?? "Dështoi ruajtja e profilit.");
    } finally {
      setSaving(false);
    }
  };

  if (!hasClinic) return null;

  const savedLogoUrl = getLogoFullUrl(profile?.logoUrl ?? profile?.LogoUrl);
  const fullLogoUrl = logoPreview
    || (savedLogoUrl
      ? `${savedLogoUrl}${savedLogoUrl.includes("?") ? "&" : "?"}v=${logoStamp}`
      : null);
  const heroName = form.name.trim() || saved.name || "Klinika";

  return (
    <>
      <Notification
        visible={notif.visible}
        type={notif.type}
        message={notif.message}
        onClose={() => setNotif((p) => ({ ...p, visible: false }))}
      />

      <div className="page-shell max-w-5xl">
        {loading ? (
          <LoadingSpinner className="py-16" label="Duke ngarkuar profilin…" />
        ) : !profile ? (
          <div className="card">
            <EmptyState
              icon={FiCheckCircle}
              title="Profili nuk u ngarkua"
              description="Nuk mund të ngarkohet profili i klinikës. Provoni të rifreskoni faqen."
            />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white px-5 py-8 sm:px-10">
            <div className="mb-10 text-center">
              {fullLogoUrl ? (
                <img
                  src={fullLogoUrl}
                  alt=""
                  className="mx-auto h-24 w-24 rounded-full border border-slate-200 bg-amber-300 object-contain"
                />
              ) : (
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-amber-300 text-2xl font-semibold text-slate-900">
                  {getInitials(heroName)}
                </div>
              )}
              <h1 className="mt-4 inline-flex items-center gap-1.5 text-xl font-semibold tracking-tight text-slate-900">
                {heroName}
                <FiCheckCircle className="text-sky-500" size={18} aria-label="Aktive" />
              </h1>
              <p className="mt-1 text-sm text-slate-500">{form.phone.trim() || saved.phone || "Klinikë e regjistruar"}</p>
            </div>

            <div className="grid gap-x-16 gap-y-10 md:grid-cols-2">
              <section>
                <h2 className="mb-1 text-sm font-semibold text-slate-800">Të dhënat e klinikës</h2>
                <ProfileText label="Emri">
                  <input
                    type="text"
                    value={form.name}
                    maxLength={200}
                    aria-label="Emri i klinikës"
                    disabled={saving}
                    onChange={setField("name")}
                    placeholder="Emri i klinikës"
                    className={textFieldClass}
                  />
                </ProfileText>
                <ProfileText label="Adresa">
                  <textarea
                    value={form.address}
                    maxLength={300}
                    rows={1}
                    aria-label="Adresa"
                    disabled={saving}
                    ref={(node) => {
                      if (!node) return;
                      node.style.height = "auto";
                      node.style.height = `${node.scrollHeight}px`;
                    }}
                    onChange={setField("address")}
                    placeholder="Rruga, qyteti"
                    className={`${textFieldClass} resize-none overflow-hidden`}
                  />
                </ProfileText>
                <ProfileText label="Telefoni">
                  <input
                    type="tel"
                    value={form.phone}
                    maxLength={50}
                    aria-label="Telefoni"
                    disabled={saving}
                    onChange={setField("phone")}
                    placeholder="+383 XX XXX XXX"
                    className={textFieldClass}
                  />
                </ProfileText>
              </section>

              <section>
                <h2 className="mb-1 text-sm font-semibold text-slate-800">Identiteti</h2>
                <ProfileRow label="Logotipi">
                  <div className="flex items-center justify-end gap-3">
                    {fullLogoUrl ? (
                      <img src={fullLogoUrl} alt="" className="h-10 max-w-[7.5rem] object-contain" />
                    ) : (
                      <span className="font-normal text-slate-400">Mungon</span>
                    )}
                    <label className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-sky-600 hover:text-sky-700">
                      <FiUpload size={14} aria-hidden />
                      {saving ? "Duke ruajtur…" : fullLogoUrl ? "Ndrysho" : "Ngarko"}
                      <input
                        type="file"
                        accept={ALLOWED_LOGO_TYPES}
                        className="sr-only"
                        disabled={saving}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          e.target.value = "";
                          if (file) setLogoFile(file);
                        }}
                      />
                    </label>
                  </div>
                </ProfileRow>
                <ProfileText label="Përshkrimi">
                  <textarea
                    value={form.description}
                    maxLength={2000}
                    rows={1}
                    aria-label="Përshkrimi"
                    disabled={saving}
                    ref={(node) => {
                      if (!node) return;
                      node.style.height = "auto";
                      node.style.height = `${node.scrollHeight}px`;
                    }}
                    onChange={setField("description")}
                    placeholder="Përshkrim i shkurtër"
                    className={`${textFieldClass} resize-none overflow-hidden`}
                  />
                </ProfileText>
              </section>
            </div>

            <div className="mt-10 grid gap-x-16 gap-y-10 md:grid-cols-2">
              <section>
                <h2 className="mb-1 text-sm font-semibold text-slate-800">Shenjat vitale</h2>
                {VITAL_OPTIONS.map(({ key, label }) => (
                  <ProfileRow key={key} label={label}>
                    <PrefCheckbox
                      label={label}
                      checked={!!vitalPrefs[key]}
                      disabled={saving}
                      onChange={(value) => setVitalPrefs((current) => ({ ...current, [key]: value }))}
                    />
                  </ProfileRow>
                ))}
              </section>

              <section>
                <h2 className="mb-1 text-sm font-semibold text-slate-800">Numri i protokollit</h2>
                <ProfileRow label="Numri i protokollit">
                  <PrefCheckbox
                    label="Numri i protokollit"
                    checked={!!protocolPrefs.useProtocolNumber}
                    disabled={saving}
                    onChange={(value) =>
                      setProtocolPrefs((current) => ({ ...current, useProtocolNumber: value }))
                    }
                  />
                </ProfileRow>
                {protocolPrefs.useProtocolNumber ? (
                  <>
                    <ProfileRow label="Numri i radhës">
                      <input
                        type="text"
                        value={protocolPrefs.nextProtocolNumber || ""}
                        disabled={saving}
                        onChange={(e) =>
                          setProtocolPrefs((current) => ({
                            ...current,
                            nextProtocolNumber: e.target.value,
                          }))
                        }
                        placeholder="100"
                        className={fieldClass}
                        aria-label="Numri i radhës"
                      />
                    </ProfileRow>
                    <ProfileRow label="Infermieri">
                      <PrefCheckbox
                        label="Infermieri"
                        checked={!!protocolPrefs.allowNurseToSet}
                        disabled={saving}
                        onChange={(value) =>
                          setProtocolPrefs((current) => ({ ...current, allowNurseToSet: value }))
                        }
                      />
                    </ProfileRow>
                    <ProfileRow label="Mjeku">
                      <PrefCheckbox
                        label="Mjeku"
                        checked={!!protocolPrefs.allowDoctorToSet}
                        disabled={saving}
                        onChange={(value) =>
                          setProtocolPrefs((current) => ({ ...current, allowDoctorToSet: value }))
                        }
                      />
                    </ProfileRow>
                  </>
                ) : null}
              </section>
            </div>

            <section className="mt-10">
              <h2 className="mb-1 text-sm font-semibold text-slate-800">Raporti PDF</h2>
              <ProfileRow label="Shenja e logos">
                <PrefCheckbox
                  label="Shfaq shenjën e logos në PDF"
                  checked={showPdfWatermark}
                  disabled={saving}
                  onChange={setShowPdfWatermark}
                />
              </ProfileRow>
            </section>

            <section className="mt-10">
              <h2 className="mb-3 text-sm font-semibold text-slate-800">Ngjyrat</h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {COLOR_THEME_OPTIONS.map((option) => {
                  const selected = (colorThemePrefs.themeId || "default") === option.id;
                  const preview = getThemePreviewColors(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={saving}
                      onClick={() => setColorThemePrefs({ themeId: option.id })}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-left ${
                        selected ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <span className="flex h-8 w-12 shrink-0 overflow-hidden rounded border border-slate-200" aria-hidden>
                        <span className="flex-1" style={{ background: preview.light }} />
                        <span className="flex-1" style={{ background: preview.main }} />
                        <span className="flex-1" style={{ background: preview.dark }} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-900">{option.label}</span>
                        <span className="block text-xs font-normal text-slate-500">{option.description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {dirty ? (
              <div className="mt-8 flex justify-end">
                <button type="button" onClick={handleSave} disabled={saving} className="btn-primary btn-md">
                  {saving ? "Duke ruajtur…" : "Ruaj"}
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
